import { LongLayoverIcon, ShortLayoverIcon } from '@/src/assets/svgs/LayoverIcons'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useGetRTSolutionCardData } from '@/src/hooks/srp/RTSolutionView/getRTSolutionCardData'
import { FlightCard } from '@/src/types/flight'
import { getAirlineIcons } from '@/src/utils/confirmation/airlineUtils'
import { getFormattedDate } from '@/src/utils/date'
import { replacePlaceHolderString } from '@/src/utils/generalUtils'
import CircleIcon from '@mui/icons-material/Circle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import NoLuggageOutlinedIcon from '@mui/icons-material/NoLuggageOutlined'
import { Box, IconButton, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { FC, useCallback } from 'react'
interface RtSolutionCardProps {
    card: FlightCard
    onSelectCard?: (card: FlightCard) => void
    onViewDetailsClick?: (card: FlightCard[], showBaggage?: boolean) => void
    loading?: boolean
}

const RtSolutionCard: FC<RtSolutionCardProps> = ({ card, onSelectCard, onViewDetailsClick, loading }) => {
    const tCommon = useTranslations('common')
    const tFlightDetails = useTranslations('srpFlightDetails')
    const { formatCurrency } = useFormatCurrencyForSrp()
    const flightData = useGetRTSolutionCardData(card)
    const { formatDate } = useFormatDate()
    const handleSelectCard = useCallback(() => {
        !loading && onSelectCard?.(card)
    }, [card, onSelectCard, loading])

    const handleViewDetailsClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()
            !loading && onViewDetailsClick?.([card], false)
        },
        [card, onViewDetailsClick, loading]
    )

    return (
        <Box>
            {/* {flightData.priceNote && (
                <Box
                    sx={{
                        background: 'linear-gradient(to right, white, #EFF6FF)',
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#184E91',
                        p: 0.5,
                        textAlign: 'right'
                    }}
                >
                    {flightData.priceNote}
                </Box>
            )} */}
            <Box
                onClick={handleSelectCard}
                sx={{
                    bgcolor: 'white',
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    position: 'relative',
                    elevation: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 121
                }}
            >
                {/* Row 1: */}
                <Box display="flex" alignItems="center" justifyContent="space-between" pb={0.5}>
                    <Box display="flex" alignItems="center">
                        <Box display={'flex'} maxWidth={150} overflow={'auto'} gap={0.5}>
                            {getAirlineIcons(flightData.airlineCodes, 24, 24)}
                        </Box>
                        <Typography
                            sx={{
                                mx: 1,
                                fontSize: 12,
                                textTransform: 'capitalize',
                                color: '#1F1F1F',

                                fontWeight: 500
                            }}
                        >
                            {flightData.airlineName}
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                        <IconButton onClick={handleViewDetailsClick} sx={{ p: 0.5 }}>
                            <InfoOutlinedIcon sx={{ color: '#5c6672', fontSize: 20 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Row 2:  */}
                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <Typography
                            sx={{
                                fontSize: 16,
                                color: '#1F1F1F',

                                fontWeight: 700
                            }}
                        >
                            {flightData.departureTime} – {flightData.arrivalTime}
                        </Typography>
                        {flightData.isNextDayCallOut && (
                            <Typography
                                sx={{
                                    display: 'flex',
                                    padding: '1px 4px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '10px',
                                    borderRadius: '4px',
                                    background: '#EFF6FF',
                                    color: '#184E91',
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    marginLeft: '4px'
                                }}
                            >
                                {formatDate(getFormattedDate(flightData.arrivalDate, 'MM/DD/YYYY'), 'D MMM')}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {typeof flightData.seatsLeft === 'number' &&
                            flightData.seatsLeft > 0 &&
                            flightData.seatsLeft <= 5 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: '68px',
                                        padding: '4px 8px',
                                        alignItems: 'center',
                                        gap: '4px',
                                        borderRadius: '4px',
                                        background: '#F8E8E8',
                                        whiteSpace: 'nowrap',
                                        color: '#B31F1F',

                                        fontSize: '10px',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        lineHeight: 'normal'
                                    }}
                                >
                                    {flightData.seatsLeft}{' '}
                                    {flightData.seatsLeft > 1
                                        ? tFlightDetails('seatLeft', { s: 's' })
                                        : tFlightDetails('seatLeft', { s: '' })}
                                </Box>
                            )}
                        <Typography
                            sx={{
                                whiteSpace: 'nowrap',
                                color: '#1F1F1F',
                                textAlign: 'right',
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: 700
                            }}
                        >
                            {flightData.pricePrefix} {formatCurrency(flightData.price)}
                        </Typography>
                    </Box>
                </Box>

                {/* Row 3:  */}
                <Box display="flex" alignItems="start" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <Typography
                            sx={{
                                color: flightData.isNbDeparture ? '#184E91' : '#1F1F1F',
                                background: flightData.isNbDeparture ? '#EFF6FF' : 'transparent',
                                px: flightData.isNbDeparture ? 1 : 0,
                                borderRadius: flightData.isNbDeparture ? '4px' : 0,
                                display: flightData.isNbDeparture ? 'inline-block' : 'inline',
                                fontSize: '12px',
                                fontWeight: 500
                            }}
                        >
                            {flightData.departureAirport}
                        </Typography>
                        {flightData.via && (
                            <>
                                <Typography variant="body2" fontWeight={500} color="text.primary" mx={0.5}>
                                    –
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{
                                        color: '#7E7E7E',
                                        fontSize: '12px',
                                        fontWeight: 500
                                    }}
                                >
                                    {flightData.via}
                                </Typography>
                            </>
                        )}
                        <Typography variant="body2" fontWeight={500} color="text.primary" mx={0.5}>
                            –
                        </Typography>
                        <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                                color: flightData.isNbArrival ? '#184E91' : '#1F1F1F',
                                background: flightData.isNbArrival ? '#EFF6FF' : 'transparent',
                                px: flightData.isNbArrival ? 1 : 0,
                                borderRadius: flightData.isNbArrival ? '4px' : 0,
                                display: flightData.isNbArrival ? 'inline-block' : 'inline',
                                fontSize: '12px',
                                fontWeight: 500
                            }}
                        >
                            {flightData.arrivalAirport}
                        </Typography>
                        {flightData.longLayover && <LongLayoverIcon style={{ margin: '0px 5px' }} />}
                        {flightData.shortLayover && <ShortLayoverIcon style={{ margin: '0px 5px' }} />}
                    </Box>
                    {flightData.strikeOffPrice && (
                        <Box display="flex" alignItems="center">
                            <Typography
                                sx={{
                                    color: '#1F1F1F',
                                    textAlign: 'right',
                                    fontSize: '12px',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    textDecorationLine: 'line-through'
                                }}
                            >
                                {formatCurrency(flightData.strikeOffPrice)}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Row 4:  */}
                {flightData.couponDetails && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography
                            variant="caption"
                            fontSize={12}
                            sx={{
                                color: '#16a34a',
                                fontWeight: 500,
                                textAlign: 'right',
                                lineHeight: 1.3
                            }}
                        >
                            {replacePlaceHolderString(flightData.couponDetails?.message, flightData.couponDetails)}
                        </Typography>
                    </Box>
                )}

                {/* Row 5: ) */}
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                        sx={{

                            fontSize: '12px',
                            fontStyle: 'normal',
                            lineHeight: 'normal'
                        }}
                        color={flightData.stops === 0 ? ' #16a34a' : '#1F1F1F'}
                        fontWeight={flightData.stops === 0 ? 600 : 500}
                    >
                        {flightData.stops === 0
                            ? tCommon('direct')
                            : `${flightData.stops} ${flightData.stops > 1 ? tCommon('stops', { s: 's' }) : tCommon('stops', { s: '' })}`}
                    </Typography>
                    <CircleIcon sx={{ fontSize: 4, color: 'grey.600' }} />
                    <Typography
                        sx={{
                            color: '#1F1F1F',

                            fontSize: '12px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: 'normal'
                        }}
                    >
                        {flightData.duration}
                    </Typography>
                    {flightData.noBaggage && (
                        <>
                            <CircleIcon sx={{ fontSize: 4, color: 'grey.600' }} />
                            <NoLuggageOutlinedIcon fontSize='small' sx={{ color: 'grey.600' }} />
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default RtSolutionCard
